/**
 * Header Logo Scroll Animation
 * Scales down logo_main based on scroll position
 */

class HeaderAnimation {
  constructor() {
    this.header = document.querySelector('header');
    this.logo = document.querySelector('.logo');
    this.logoMain = document.querySelector('.logo_main');
    this.logoBaseline = document.querySelector('.logo_baseline');

    if (!this.header || !this.logo || !this.logoMain || !this.logoBaseline) return;

    // Configuration
    this.config = {
      startWidth: 260,      // Initial SVG width in px
      endWidth: 190,        // Target width in px
      scrollRange: 120,     // Scroll distance in px over which animation happens
      baselineDistance: 12, // Distance in px the baseline moves down
      startTop: 20,         // Initial top position in px
      endTop: 0,            // Final top position in px
      bgScrollDesktop: 100, // Scroll amount for background class on desktop (768px+)
      bgScrollMobile: 80   // Scroll amount for background class on mobile (<768px)
    };

    this.init();
  }

  init() {
    // Use passive listener for better scroll performance
    window.addEventListener('scroll', () => this.handleScroll(), { passive: true });

    // Listen for resize to check viewport
    window.addEventListener('resize', () => this.handleScroll(), { passive: true });

    // Initial state
    this.handleScroll();
  }

  handleScroll() {
    const scrollY = window.scrollY;
    const isDesktop = window.innerWidth >= 768;

    // Add background class (applies to all viewport sizes with different scroll amounts)
    const bgScrollThreshold = isDesktop ? this.config.bgScrollDesktop : this.config.bgScrollMobile;
    if (scrollY >= bgScrollThreshold) {
      this.header.classList.add('has-background');
    } else {
      this.header.classList.remove('has-background');
    }

    // Only apply animations on viewports 768px and above
    if (window.innerWidth < 768) {
      this.resetToInitialState();
      return;
    }

    // Calculate progress (0 to 1) based on scroll position
    const progress = Math.min(scrollY / this.config.scrollRange, 1);

    // Calculate current width using linear interpolation
    const currentWidth = this.config.startWidth - (this.config.startWidth - this.config.endWidth) * progress;

    // Calculate scale factor for logo_main
    // Since the SVG container scales to currentWidth, logo_main needs to maintain its visual size
    const scaleFactor = currentWidth / this.config.startWidth;

    // Calculate baseline translateY (moves down)
    const baselineTranslateY = progress * this.config.baselineDistance;

    // Calculate baseline opacity (fade out)
    const baselineOpacity = 1 - progress;

    // Calculate baseline blur (0 to 10px)
    const baselineBlur = progress * 10;

    // Calculate logo top position
    const currentTop = this.config.startTop - (this.config.startTop - this.config.endTop) * progress;

    // Apply transforms
    this.logo.style.width = `${currentWidth}px`;
    this.logo.style.top = `${currentTop}px`;
    this.logoMain.style.transformOrigin = 'center';
    this.logoMain.style.transform = `scale(${scaleFactor})`;
    this.logoBaseline.style.transform = `translateY(${baselineTranslateY}px)`;
    this.logoBaseline.style.opacity = baselineOpacity;
    this.logoBaseline.style.filter = `blur(${baselineBlur}px)`;

    // Add class when scrolled for additional styling
    if (progress > 0.05) {
      this.header.classList.add('is-scrolled');
    } else {
      this.header.classList.remove('is-scrolled');
    }
  }

  resetToInitialState() {
    // Remove inline styles on mobile to let CSS take over
    this.logo.style.width = '';
    this.logo.style.top = '';
    this.logoMain.style.transform = '';
    this.logoBaseline.style.transform = '';
    this.logoBaseline.style.opacity = '';
    this.logoBaseline.style.filter = '';
    this.header.classList.remove('is-scrolled');
    // Note: has-background class is handled separately and applies to all viewports
  }
}

/**
 * Pop Container Animation
 * Adds 'animate-in' class when element comes into view
 */
class PopAnimation {
  constructor() {
    this.popContainers = document.querySelectorAll('.pop-container');

    if (!this.popContainers.length) return;

    this.init();
  }

  init() {
    const observerOptions = {
      threshold: 1,
      rootMargin: '0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.classList.contains('animate-in')) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    this.popContainers.forEach(container => {
      observer.observe(container);
    });
  }
}

/**
 * Collection Nav Scroll
 * Scrolls active collection link into view on mobile
 */
class CollectionNavScroll {
  constructor() {
    this.nav = document.querySelector('.collections-container nav');
    this.activeLink = this.nav?.querySelector('a.active');

    if (!this.nav || !this.activeLink) return;

    this.init();
  }

  init() {
    // Only scroll on mobile (below 768px)
    if (window.innerWidth >= 768) return;

    // Scroll active link into view, centered
    this.activeLink.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    });
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  new HeaderAnimation();
  new PopAnimation();
  new CollectionNavScroll();
});
